import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UsersService } from 'src/app/services/users.service';
import { ClassesService } from 'src/app/services/classes.service';
import { SubjectsService } from 'src/app/services/subjects.service';
import { MajorsService } from 'src/app/services/majors.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from 'src/app/constants/app-constants';

interface ClassGroup {
  majorId: string;
  majorName: string;
  classes: { id: string; name: string }[];
}

@Component({
  selector: 'app-users-add',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.css']
})
export class UserAddComponent {
  @Input() id: string = '';
  @Output() idChange = new EventEmitter<string>();
  @Input() open: boolean = false;
  @Output() openChange = new EventEmitter<boolean>();
  @Input() mode: string = '';
  @Output() modeChange = new EventEmitter<string>();
  @Output() userUpdated = new EventEmitter<any>();

  form!: FormGroup;
  showPassword: boolean = false;
  isError: boolean = false;
  errorMessage = '';
  Title: string = ''
  isSystemUser = false;
  systemUserRoleName = '';
  /** Keeps the form hidden until the user is loaded, so system-user fields don't flicker in. */
  loadingDetails = false;
  roles: any[] = [];
  classGroups: ClassGroup[] = [];
  allSubjects: any[] = [];
  constructor(
    public usersService: UsersService,
    private classesService: ClassesService,
    private subjectsService: SubjectsService,
    private majorsService: MajorsService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  /** Classes and subjects only apply to teaching staff, not to admins/supervisors. */
  get showAssignments(): boolean {
    if (this.mode === 'edit' && this.isSystemUser) {
      return false;
    }
    return !this.isNonTeachingRoleSelected;
  }

  private get isNonTeachingRoleSelected(): boolean {
    const roleId = String(this.form?.getRawValue()?.role || '');
    const role = this.roles.find(item => this.roleId(item) === roleId);
    const roleName = String(role?.name || '').toLowerCase();
    return AppConstants.ADMIN_ROLES.includes(roleName)
      || roleName === AppConstants.SUPERVISOR_ROLE;
  }

  /** Roles API historically returned `_id`; normalize so the select matches patched values. */
  roleId(role: { id?: string; _id?: string } | null | undefined): string {
    return String(role?.id ?? role?._id ?? '');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open']?.currentValue === true) {
      if (this.mode === 'edit') {
        this.Title = 'USERS.EDIT_TITLE';
        this.isError = false;
        void this.openEditDialog();
      } else {
        this.Title = 'USERS.ADD_TITLE';
        this.isError = false;
        this.isSystemUser = false;
        this.systemUserRoleName = '';
        this.loadingDetails = false;
        this.initForm();
      }
      return;
    }

    if (changes['open']?.currentValue === false) {
      this.isError = false;
      this.errorMessage = '';
      return;
    }

    if (changes['id'] && this.open && this.mode === 'edit' && this.id) {
      void this.openEditDialog();
    }
  }

  private async openEditDialog() {
    if (!this.id) {
      this.displayError('USERS.ID_MISSING');
      return;
    }

    if (!this.form) {
      this.initForm();
    }

    this.loadingDetails = true;
    try {
      await Promise.all([
        this.loadRoles(),
        this.loadClassesAndSubjects()
      ]);
      await this.loadUserDetails();
    } finally {
      this.loadingDetails = false;
    }
  }

  initForm() {
    this.form = new FormGroup({
      username: new FormControl('', [Validators.required]),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      password: new FormControl(''),
      role: new FormControl('', [Validators.required]),
      classes: new FormControl<string[]>([]),
      subjects: new FormControl<string[]>([])
    });
    this.loadRoles();
    this.loadClassesAndSubjects();
  }

  async loadClassesAndSubjects() {
    try {
      const [classes, majors] = await Promise.all([
        this.classesService.list(),
        this.majorsService.list()
      ]);
      this.classGroups = this.buildClassGroups(classes, majors);
      this.allSubjects = await this.subjectsService.list();
    } catch (error) {
      console.error('Failed to load classes or subjects:', error);
    }
  }

  buildClassGroups(classes: any[], majors: any[]): ClassGroup[] {
    const grouped = new Map<string, ClassGroup>();

    for (const cls of classes) {
      const majorId = cls.major?.id || 'unassigned';
      const majorName = cls.major?.name || this.translate.instant('USERS.UNASSIGNED_MAJOR');

      if (!grouped.has(majorId)) {
        grouped.set(majorId, { majorId, majorName, classes: [] });
      }

      grouped.get(majorId)!.classes.push({ id: cls.id, name: cls.name });
    }

    const orderedGroups: ClassGroup[] = majors
      .map(major => grouped.get(major.id))
      .filter((group): group is ClassGroup => !!group && group.classes.length > 0);

    const unassigned = grouped.get('unassigned');
    if (unassigned?.classes.length) {
      orderedGroups.push(unassigned);
    }

    for (const group of orderedGroups) {
      group.classes.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    }

    return orderedGroups;
  }

  isClassSelected(classId: string): boolean {
    return (this.form.get('classes')?.value || []).includes(classId);
  }

  isSubjectSelected(subjectId: string): boolean {
    return (this.form.get('subjects')?.value || []).includes(subjectId);
  }

  toggleClass(classId: string, checked: boolean) {
    if (this.isSystemUser) {
      return;
    }
    const current: string[] = [...(this.form.get('classes')?.value || [])];
    const next = checked
      ? [...current, classId]
      : current.filter(id => id !== classId);
    this.form.patchValue({ classes: next });
  }

  toggleSubject(subjectId: string, checked: boolean) {
    if (this.isSystemUser) {
      return;
    }
    const current: string[] = [...(this.form.get('subjects')?.value || [])];
    const next = checked
      ? [...current, subjectId]
      : current.filter(id => id !== subjectId);
    this.form.patchValue({ subjects: next });
  }

  async loadUserDetails() {
    if (!this.id) {
      return;
    }

    this.isError = false;
    try {
      const user = await this.usersService.getUserById(this.id);
      if (!user) {
        this.displayError('USERS.NOT_FOUND');
        return;
      }

      if (!this.form) {
        this.initForm();
      }

      this.isSystemUser = user.isSystemUser === true || user.username === 'config';
      this.systemUserRoleName = user.role?.name || 'admin';
      this.form.patchValue({
        username: user.username ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        password: '',
        role: this.roleId(user.role),
        classes: (user.classes || []).map((item: any) => String(item.id ?? item._id)),
        subjects: (user.subjects || []).map((item: any) => String(item.id ?? item._id))
      });
      this.applySystemUserFieldState();
    } catch (error: any) {
      const message = error?.error?.errorMessages?.[0]?.message
        || error?.message
        || 'USERS.LOAD_FAILED';
      this.displayError(message);
    }
  }

  async submit() {
    this.form.markAllAsTouched();
    if (!this.isFormValid()) {
      this.displayError('COMMON.REQUIRED_FIELDS');
      return;
    }

    this.isError = false;
    if (this.mode == "edit") {
      await this.editUser();
    } else {
      await this.addUser();
    }
  }

  async getRoles() {
    try {
      let roles = await this.usersService.getRoles();
      return roles;
    } catch (error: any) {
      this.displayError(error.message);
    }
  }

  async loadRoles() {
    try {
      const roles = await this.getRoles() || [];
      this.roles = roles.map((role: any) => ({
        ...role,
        id: this.roleId(role),
      }));
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  }

  async addUser() {
    try {
      let user: any = await this.usersService.addUser(this.buildPayload());
      this.userUpdated.emit(user);
      this.resetForm();
    } catch (error: any) {
      this.displayError(error.message);
    }
  }

  async editUser() {
    try {
      const payload = this.buildPayload();
      if (this.isSystemUser) {
        delete payload.role;
        delete payload.classes;
        delete payload.subjects;
      }
      let user: any = await this.usersService.editUser(this.id, payload);
      this.userUpdated.emit(user);
      this.resetForm();
    } catch (error: any) {
      this.displayError(error.message);
    }
  }

  displayError(errorMessage: string) {
    this.isError = true;
    this.errorMessage = errorMessage;
  }

  resetForm() {
    this.open = false;
    this.isError = false;
    this.errorMessage = '';
    this.isSystemUser = false;
    this.systemUserRoleName = '';
    this.modeChange.emit('add');
    this.openChange.emit(false);
    this.form?.reset({
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      role: '',
      classes: [],
      subjects: []
    });
    this.form?.get('role')?.enable();
    this.form?.get('classes')?.enable();
    this.form?.get('subjects')?.enable();
  }

  /** Include disabled controls (e.g. locked role) and drop blank password on edit. */
  private buildPayload(): any {
    const payload: any = { ...this.form.getRawValue() };
    if (!this.showAssignments) {
      payload.classes = [];
      payload.subjects = [];
    }
    if (!payload.password) {
      delete payload.password;
    }
    return payload;
  }

  private applySystemUserFieldState(): void {
    const isLocked = this.mode === 'edit' && this.isSystemUser;
    const fields = ['role', 'classes', 'subjects'];

    for (const field of fields) {
      const control = this.form.get(field);
      if (!control) {
        continue;
      }

      if (isLocked) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  private isFormValid(): boolean {
    const username = String(this.form.getRawValue()?.username || '').trim();
    const usernameOk = !!username;
    const roleValue = String(this.form.getRawValue()?.role || '');
    const roleOk = this.mode === 'edit' && this.isSystemUser
      ? true
      : !!roleValue;

    return usernameOk && roleOk;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
